import { NavigationGuardNext, Route } from 'vue-router';
import {
  canSelfServeSubscription,
  hasPermission,
  hasSubscription,
  isOnFreePlan,
  isSubscriptionPastDue,
  justFinishedFreeTrial,
  loggedIn,
  organizationHasAddedPaymentMethod,
  Permissions,
  user,
} from '@/ui/auth/auth';
import { NamedLocation, RouteName } from '../types';
import { BulmaBreakPoints } from '@/ui/types';
import { screenWidth } from '@/ui/helpers';
import { isDisableFreePlanEnabled } from '@/ui/helpers/featureHelper';

const routesToIgnoreMobile: RouteName[] = ['subscriptionEndedPage', 'noPlanPage'];
const isIgnoredRouteMobileRedirect = (routeName: RouteName) => routesToIgnoreMobile.includes(routeName);
const shouldRedirectToMobileWelcome = (
  (to: Route) => screenWidth < BulmaBreakPoints.Desktop && !['welcomePageMobile', 'onboarding'].includes(to.name!)
);
const isIgnoredSubscriptionChecking = (to: Route) => [
  'administration',
  'subscribe',
  'freePlanEnded',
  'logout',
].includes(to.name!);

export function guardAuthenticatedRoutes(
  to: Route,
  from: Route,
  next: NavigationGuardNext,
): void {
  // check authentication
  if (!loggedIn.value) {
    const redirectTo: NamedLocation = {
      name: 'login',
      query: { from: window.location.pathname },
    };
    next(redirectTo);
  }

  if (isIgnoredSubscriptionChecking(to) || (user.value.organization.paysByInvoice && user.value.subscription)) {
    // https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
    // Make sure that the next function is called exactly once in any given
    // pass through the navigation guard. It can appear more than once,
    // but only if the logical paths have no overlap, otherwise the
    // hook will never be resolved or produce errors.
    // That's why we return right after next()
    next();

    return;
  }

  const subscribePage: NamedLocation = { name: 'subscribe' };
  const noPlanPageLocation: NamedLocation = { name: 'noPlanPage' };
  const subscriptionEndedPageRoute: NamedLocation = { name: 'subscriptionEndedPage' };
  const organizationHasSubscription = hasSubscription();
  const hasManageBillingPermission = hasPermission(Permissions.MANAGE_BILLING);
  const organizationHasSubscriptionPastDue = isSubscriptionPastDue();
  const pastDueBillingPage: NamedLocation = { name: 'pastDueNoManageBillingPermission' };
  const hasAddedPaymentMethod = organizationHasAddedPaymentMethod();
  const endFreePlanPage: NamedLocation = { name: 'freePlanEnded' };

  if (isDisableFreePlanEnabled() && organizationHasSubscription && isOnFreePlan.value) {
    next(endFreePlanPage);

    return;
  }

  if (
    organizationHasSubscription
    && !hasManageBillingPermission
    && organizationHasSubscriptionPastDue
    && to.name !== pastDueBillingPage.name
  ) {
    next(pastDueBillingPage);
  }

  if (
    organizationHasSubscription
    && hasManageBillingPermission
    && organizationHasSubscriptionPastDue
    && !hasAddedPaymentMethod
    && to.name !== subscribePage.name
  ) {
    if (canSelfServeSubscription.value) {
      next(subscribePage);
      return;
    }

    next(noPlanPageLocation);
    return;
  }

  if (!organizationHasSubscription) {
    if (hasManageBillingPermission && to.name !== subscribePage.name && canSelfServeSubscription.value) {
      next(subscribePage);
    }

    if (to.name !== noPlanPageLocation.name && !justFinishedFreeTrial.value) {
      next(noPlanPageLocation);
    }
  }

  if (justFinishedFreeTrial.value) {
    if (to.name !== 'subscribe' && to.name !== 'subscriptionEndedPage') {
      if (hasManageBillingPermission && canSelfServeSubscription.value) {
        next({ name: 'subscribe' });
        return;
      }

      next(subscriptionEndedPageRoute);
    }
  }

  const welcomePageMobileRoute: NamedLocation = { name: 'welcomePageMobile' };
  // if user is in a mobile we will lock him to the welcome screen
  if (shouldRedirectToMobileWelcome(to) && !isIgnoredRouteMobileRedirect(to.name as RouteName)) {
    next(welcomePageMobileRoute);
  }

  next();
}
